import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent } from "react";
import { ChevronLeft, ChevronRight, Crosshair } from "lucide-react";
import type { ReactNode } from "react";
import { useDisplayTimezone } from "@nexus/ui";
import type { TimelineEvent, TimelineLabelMode } from "../contracts/event-timeline";
import {
  clamp,
  formatTimeTick,
  formatTimelineDay,
  HOUR_MS,
  isOngoing,
  MINUTE_MS,
  packLanes,
  parseDbTime,
} from "./helpers";
import { DEFAULT_TIMELINE_LABELS, type EventTimelineLabels } from "./labels";
import { TimelineEventCard } from "./TimelineEventCard";

const LANE_HEIGHT = 36;
const CARD_INSET = 2;
const DRAG_THRESHOLD_PX = 4;
const MIN_VISIBLE_LANES = 4;
const MAX_MAJOR_TICKS = 7;
const SPAN_PRESETS = [3, 6, 12, 24] as const;

const TICK_INTERVALS_MS = [
  5 * MINUTE_MS,
  15 * MINUTE_MS,
  30 * MINUTE_MS,
  HOUR_MS,
  2 * HOUR_MS,
  4 * HOUR_MS,
  8 * HOUR_MS,
  12 * HOUR_MS,
  24 * HOUR_MS,
  2 * 24 * HOUR_MS,
  7 * 24 * HOUR_MS,
];

interface TimelineViewProps {
  events: TimelineEvent[];
  nowMs: number;
  spanMs: number;
  labelMode: TimelineLabelMode;
  centerMs: number;
  onCenterChange: (updater: (center: number) => number) => void;
  onSpanChange: (spanMs: number) => void;
  onJumpToNow: () => void;
  selectedEventId?: string;
  interactive: boolean;
  onSelectEvent: (event: TimelineEvent) => void;
  labels?: EventTimelineLabels;
}

function pickMajorInterval(spanMs: number): number {
  return (
    TICK_INTERVALS_MS.find((interval) => spanMs / interval <= MAX_MAJOR_TICKS) ??
    TICK_INTERVALS_MS[TICK_INTERVALS_MS.length - 1]
  );
}

function buildTicks(from: number, to: number, interval: number): number[] {
  const first = Math.ceil(from / interval) * interval;
  const result: number[] = [];
  for (let t = first; t <= to; t += interval) result.push(t);
  return result;
}

function formatWindowSpan(spanMs: number): string {
  if (spanMs % HOUR_MS === 0) {
    return `${spanMs / HOUR_MS}h`;
  }
  if (spanMs % MINUTE_MS === 0) {
    return `${spanMs / MINUTE_MS}m`;
  }
  return `${Math.round(spanMs / MINUTE_MS)}m`;
}

function PillGroup({ children }: { children: ReactNode }) {
  return (
    <div className="inline-flex h-6 items-center rounded-full border border-border bg-surface/90 p-0.5">
      {children}
    </div>
  );
}

function PillButton({
  active,
  onClick,
  label,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-semibold transition-colors ${
        active
          ? "bg-brand-cyan/12 text-brand-cyan"
          : "text-muted hover:bg-surface-elevated hover:text-primary"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * The timeline answers "when". Drag the canvas to move through time; the span
 * presets live in the header, and a single "Now" chip appears only when the
 * present has scrolled out of view.
 */
export function TimelineView({
  events,
  nowMs,
  spanMs,
  labelMode,
  centerMs,
  onCenterChange,
  onSpanChange,
  onJumpToNow,
  selectedEventId,
  interactive,
  onSelectEvent,
  labels = DEFAULT_TIMELINE_LABELS,
}: TimelineViewProps) {
  const timezone = useDisplayTimezone();
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const panAnimationRef = useRef<number | null>(null);

  const viewStart = centerMs - spanMs / 2;
  const viewEnd = centerMs + spanMs / 2;
  const leftPct = useCallback(
    (ms: number) => ((ms - viewStart) / spanMs) * 100,
    [viewStart, spanMs],
  );

  const eventsById = useMemo(() => {
    const map = new Map<string, TimelineEvent>();
    for (const event of events) map.set(event.event_id, event);
    return map;
  }, [events]);

  const spanRef = useRef(spanMs);
  spanRef.current = spanMs;
  const openRef = useRef({ onSelectEvent, eventsById, interactive });
  openRef.current = { onSelectEvent, eventsById, interactive };

  const cancelPanAnimation = useCallback(() => {
    if (panAnimationRef.current === null) return;
    window.cancelAnimationFrame(panAnimationRef.current);
    panAnimationRef.current = null;
  }, []);

  useEffect(() => () => cancelPanAnimation(), [cancelPanAnimation]);

  const animateCenterTo = useCallback(
    (targetCenter: number) => {
      cancelPanAnimation();

      const startCenter = centerMs;
      const delta = targetCenter - startCenter;
      const durationMs = Math.min(260, Math.max(160, spanRef.current * 0.08));
      const startTime = performance.now();

      const step = (frameTime: number) => {
        const progress = Math.min((frameTime - startTime) / durationMs, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const nextCenter = startCenter + delta * eased;
        onCenterChange(() => nextCenter);

        if (progress < 1) {
          panAnimationRef.current = window.requestAnimationFrame(step);
          return;
        }

        panAnimationRef.current = null;
      };

      panAnimationRef.current = window.requestAnimationFrame(step);
    },
    [cancelPanAnimation, centerMs, onCenterChange],
  );

  const jumpByWindow = useCallback(
    (direction: -1 | 1) => {
      animateCenterTo(centerMs + direction * spanRef.current);
    },
    [animateCenterTo, centerMs],
  );

  // A tap (no drag) on a bar opens its detail, anchored to the bar's own rect.
  const resolveTap = useCallback((target: EventTarget | null) => {
    const { interactive: canOpen, eventsById: byId } = openRef.current;
    if (!canOpen || !(target instanceof HTMLElement)) return;
    const card = target.closest<HTMLElement>("[data-kind='event']");
    if (!card) return;
    const event = byId.get(card.dataset.eventId ?? "");
    if (!event) return;
    openRef.current.onSelectEvent(event);
  }, []);

  const onPointerDown = useCallback(
    (down: ReactPointerEvent<HTMLDivElement>) => {
      cancelPanAnimation();
      const width = trackRef.current?.getBoundingClientRect().width ?? 1;
      const startX = down.clientX;
      const downTarget = down.target;
      let moved = false;
      let lastDelta = 0;

      const handleMove = (move: PointerEvent) => {
        const deltaPx = move.clientX - startX;
        if (Math.abs(deltaPx) > DRAG_THRESHOLD_PX) {
          moved = true;
          setDragging(true);
        }
        const step = deltaPx - lastDelta;
        lastDelta = deltaPx;
        onCenterChange((center) => center - (step / width) * spanRef.current);
      };
      const handleUp = () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        setDragging(false);
        if (!moved) resolveTap(downTarget);
      };
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
    },
    [cancelPanAnimation, onCenterChange, resolveTap],
  );

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!interactive) return;
      if (e.key !== "Enter" && e.key !== " ") return;
      const card = (e.target as HTMLElement).closest<HTMLElement>("[data-kind='event']");
      if (!card) return;
      e.preventDefault();
      const event = eventsById.get(card.dataset.eventId ?? "");
      if (!event) return;
      onSelectEvent(event);
    },
    [interactive, eventsById, onSelectEvent],
  );

  const { majorTicks, minorTicks } = useMemo(() => {
    const major = pickMajorInterval(spanMs);
    const minor = major / 2;
    const majors = buildTicks(viewStart, viewEnd, major);
    const minors = buildTicks(viewStart, viewEnd, minor).filter((tick) => tick % major !== 0);
    return { majorTicks: majors, minorTicks: minors };
  }, [viewStart, viewEnd, spanMs]);

  const visibleEvents = useMemo(
    () =>
      events.filter((event) => {
        const start = parseDbTime(event.event_start);
        const end = parseDbTime(event.event_end);
        return end >= viewStart && start <= viewEnd;
      }),
    [events, viewStart, viewEnd],
  );

  const lanes = useMemo(() => packLanes(visibleEvents), [visibleEvents]);
  const laneCount = Math.max(1, ...[...lanes.values()].map((lane) => lane + 1));
  const contentHeight = laneCount * LANE_HEIGHT;

  const nowLeft = leftPct(nowMs);
  const nowVisible = nowLeft >= 0 && nowLeft <= 100;
  const nowIsPast = nowLeft > 100; // present is to the right → scrolled into the past
  const currentDayLabel = formatTimelineDay(centerMs, timezone);
  const previousWindowLabel = `Previous ${formatWindowSpan(spanMs)}`;
  const nextWindowLabel = `Next ${formatWindowSpan(spanMs)}`;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Ruler: the scale and the present. */}
      <div className="relative mb-1 shrink-0 border-b border-border py-1 text-xs text-muted">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-20 flex items-center">
          <span className="pointer-events-auto inline-flex items-center rounded-full border border-border bg-surface px-2 py-0.5 text-xs font-medium text-primary shadow-sm shadow-black/5">
            {currentDayLabel}
          </span>
        </div>

        <div className="absolute inset-y-0 right-0 z-30 flex items-center">
          <PillGroup>
            {SPAN_PRESETS.map((hours) => (
              <PillButton
                key={hours}
                label={`Show ${hours} hours`}
                active={spanMs === hours * HOUR_MS}
                onClick={() => onSpanChange(hours * HOUR_MS)}
              >
                <span className="tabular-nums">{hours}h</span>
              </PillButton>
            ))}
          </PillGroup>
        </div>

        <div className="relative h-5 pl-[7.25rem] pr-[8.75rem]">
          {majorTicks.map((tick) => {
            const left = leftPct(tick);
            if (left < 5 || left > 95) return null;
            return (
              <span
                key={tick}
                className="absolute top-0.5 -translate-x-1/2 whitespace-nowrap tabular-nums"
                style={{ left: `${left}%` }}
              >
                {formatTimeTick(tick, spanMs, nowMs, labelMode, timezone)}
              </span>
            );
          })}

          {nowVisible && (
            <span
              className="absolute top-0.5 z-20 -translate-x-1/2 rounded bg-brand-cyan px-1.5 text-xs font-semibold leading-5 text-white"
              style={{ left: `${nowLeft}%` }}
            >
              {labels.now}
            </span>
          )}
        </div>
      </div>

      <div className="group relative min-h-0 flex-1">
        <div
          ref={trackRef}
          onPointerDown={onPointerDown}
          onKeyDown={onKeyDown}
          className={`relative h-full touch-none select-none overflow-x-hidden overflow-y-auto ${
            dragging ? "cursor-grabbing" : "cursor-grab"
          }`}
          style={{ minHeight: MIN_VISIBLE_LANES * LANE_HEIGHT }}
        >
          <div className="relative w-full" style={{ height: contentHeight, minHeight: "100%" }}>
            {minorTicks.map((tick) => {
              const left = leftPct(tick);
              if (left < 0 || left > 100) return null;
              return (
                <span
                  key={`minor-${tick}`}
                  className="absolute top-0 bottom-0 w-px bg-border/25"
                  style={{ left: `${left}%` }}
                  aria-hidden
                />
              );
            })}

            {majorTicks.map((tick) => {
              const left = leftPct(tick);
              if (left < 0 || left > 100) return null;
              return (
                <span
                  key={`major-${tick}`}
                  className="absolute top-0 bottom-0 w-px bg-border/60"
                  style={{ left: `${left}%` }}
                  aria-hidden
                />
              );
            })}

            {visibleEvents.map((event) => {
              const l = clamp(leftPct(parseDbTime(event.event_start)), 0, 100);
              const width = Math.max(clamp(leftPct(parseDbTime(event.event_end)), 0, 100) - l, 0);
              const lane = lanes.get(event.event_id) ?? 0;
              return (
                <TimelineEventCard
                  key={event.event_id}
                  event={event}
                  leftPct={l}
                  widthPct={width}
                  top={lane * LANE_HEIGHT + CARD_INSET}
                  height={LANE_HEIGHT - CARD_INSET * 2}
                  ongoing={isOngoing(event, nowMs)}
                  selected={selectedEventId === event.event_id}
                  interactive={interactive}
                />
              );
            })}

            {visibleEvents.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-sm text-muted">{labels.noEventsInWindow}</p>
              </div>
            )}

            {nowVisible && (
              <div
                className="pointer-events-none absolute top-0 bottom-0 z-20 w-px bg-brand-cyan"
                style={{ left: `${nowLeft}%` }}
                aria-hidden
              />
            )}
          </div>
        </div>

        <div className="pointer-events-none absolute inset-y-0 left-0 right-0 z-30 flex items-center justify-between px-0">
          <button
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              jumpByWindow(-1);
            }}
            aria-label={previousWindowLabel}
            title={previousWindowLabel}
            className="pointer-events-auto inline-flex h-12 w-12 items-center justify-start rounded-full bg-transparent text-muted/75 opacity-0 transition-all duration-150 ease-out hover:text-primary group-hover:opacity-100 group-focus-within:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            <ChevronLeft className="h-8 w-8" strokeWidth={1.3} aria-hidden />
          </button>

          <button
            type="button"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              jumpByWindow(1);
            }}
            aria-label={nextWindowLabel}
            title={nextWindowLabel}
            className="pointer-events-auto inline-flex h-12 w-12 items-center justify-end rounded-full bg-transparent text-muted/75 opacity-0 transition-all duration-150 ease-out hover:text-primary group-hover:opacity-100 group-focus-within:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            <ChevronRight className="h-8 w-8" strokeWidth={1.3} aria-hidden />
          </button>
        </div>

        {/* Contextual return-to-present, only when the present is off-screen. */}
        {!nowVisible && (
          <button
            type="button"
            onClick={onJumpToNow}
            className="absolute bottom-3 right-3 z-30 inline-flex items-center gap-1.5 rounded-full border border-border bg-surface/95 px-3 py-1.5 text-xs font-semibold text-brand-cyan shadow-lg shadow-black/10 backdrop-blur transition-colors hover:bg-surface-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            <Crosshair className="h-3.5 w-3.5" aria-hidden />
            {nowIsPast ? `${labels.now} →` : `← ${labels.now}`}
          </button>
        )}
      </div>
    </div>
  );
}
